import { useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Checkbox,
  Code,
  Group,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import {
  getClientConfig,
  submitClientConfig,
  validateClientConfig,
  type BiomarkerDisplayRule,
  type ClientConfig,
  type ClientConfigValidation,
  type PanelRule,
  type SubmitResult,
} from "@/api/catalogs";
import { useResolvedPermissions } from "@/auth/permissions";
import { AppLayout } from "@/components/AppLayout";
import { BiomarkerCombobox } from "@/components/BiomarkerCombobox";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";

/**
 * docs#150 items 2 and 3: the ADR-0064 per-organization presentation config.
 *
 * One route, two faces. `catalog:read` gets a read-only view of the SERVED config.
 * `catalog:author` gets the editor: per-biomarker show / hide-when-empty / order / relabel,
 * the same for panels, validate (which returns the committed YAML and a diff), then submit
 * as a merge request. Hiding the editor is a courtesy, not the control: onboarding-api
 * refuses validate and submit without `catalog:author`, and the read without `catalog:read`.
 *
 * The write routes accept a client SLUG only (the ADR-0008 grammar). A tenant's token
 * carries its organization id, so a lab admin can view its config here by id but cannot
 * submit until docs#150 item 1 lands. The page says so rather than offering a form that
 * would 400.
 */

const SLUG_RE = /^[a-z0-9-]{1,32}$/;

interface BiomarkerRow {
  symbol: string;
  include: boolean;
  drop_if_empty: boolean;
  order: number | null;
  relabel: string;
}

interface PanelRow {
  name: string;
  include: boolean;
  order: number | null;
  relabel: string;
}

function toBiomarkerRow(rule: BiomarkerDisplayRule): BiomarkerRow {
  return {
    symbol: rule.symbol,
    include: rule.include ?? true,
    drop_if_empty: rule.drop_if_empty ?? false,
    order: rule.order ?? null,
    relabel: rule.relabel ?? "",
  };
}

function toPanelRow(rule: PanelRule): PanelRow {
  return {
    name: rule.name,
    include: rule.include ?? true,
    order: rule.order ?? null,
    relabel: rule.relabel ?? "",
  };
}

/** The served fields the editor has no control for, carried through untouched. */
const CARRIED_FIELDS = [
  "approval_mode",
  "content_span",
  "default_language",
  "display_name",
  "timezone",
  "schema_version",
] as const;

/**
 * The document the editor submits. An empty relabel becomes null because the model refuses
 * an empty string (`min_length=1`), and a blank relabel means "use the catalog name". The
 * server drops defaults when it writes the YAML, so sending them is harmless.
 *
 * onboarding-api commits exactly this body, so any served field the editor does not show
 * (the Gate B lab settings and the schema version) is copied from `served`. Leaving one out
 * would delete it in the MR: an Arabic lab back to English, an XS lab back to M.
 */
export function buildClientConfig(
  org: string,
  biomarkers: BiomarkerRow[],
  panels: PanelRow[],
  served: ClientConfig | null = null,
): ClientConfig {
  const carried: Partial<ClientConfig> = {};
  for (const field of CARRIED_FIELDS) {
    const value = served?.[field];
    if (value !== undefined && value !== null) Object.assign(carried, { [field]: value });
  }
  return {
    ...carried,
    organization_id: org,
    biomarkers: biomarkers.map((b) => ({
      symbol: b.symbol,
      include: b.include,
      drop_if_empty: b.drop_if_empty,
      order: b.order,
      relabel: b.relabel.trim() === "" ? null : b.relabel.trim(),
    })),
    panels: panels.map((p) => ({
      name: p.name,
      include: p.include,
      order: p.order,
      relabel: p.relabel.trim() === "" ? null : p.relabel.trim(),
    })),
  };
}

function orderValue(value: number | string): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? Math.trunc(value) : null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function yesNo(value: boolean): string {
  return value ? t("client_config.yes") : t("client_config.no");
}

function ReadOnlyView({ config }: { config: ClientConfig }): JSX.Element {
  return (
    <Stack gap="md">
      <Title order={2}>{t("client_config.biomarkers")}</Title>
      {config.biomarkers.length === 0 ? (
        <Text c="dimmed">{t("client_config.no_biomarker_rules")}</Text>
      ) : (
        <Table striped withTableBorder aria-label={t("client_config.biomarkers")}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("client_config.col_symbol")}</Table.Th>
              <Table.Th>{t("client_config.col_shown")}</Table.Th>
              <Table.Th>{t("client_config.col_drop_if_empty")}</Table.Th>
              <Table.Th>{t("client_config.col_order")}</Table.Th>
              <Table.Th>{t("client_config.col_relabel")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {config.biomarkers.map((b) => (
              <Table.Tr key={b.symbol}>
                <Table.Td ff="monospace">{b.symbol}</Table.Td>
                <Table.Td>{yesNo(b.include ?? true)}</Table.Td>
                <Table.Td>{yesNo(b.drop_if_empty ?? false)}</Table.Td>
                <Table.Td>{b.order ?? t("client_config.unset")}</Table.Td>
                <Table.Td>{b.relabel ?? t("client_config.unset")}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <Title order={2}>{t("client_config.panels")}</Title>
      {config.panels.length === 0 ? (
        <Text c="dimmed">{t("client_config.no_panel_rules")}</Text>
      ) : (
        <Table striped withTableBorder aria-label={t("client_config.panels")}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("client_config.col_panel")}</Table.Th>
              <Table.Th>{t("client_config.col_shown")}</Table.Th>
              <Table.Th>{t("client_config.col_order")}</Table.Th>
              <Table.Th>{t("client_config.col_relabel")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {config.panels.map((p) => (
              <Table.Tr key={p.name}>
                <Table.Td>{p.name}</Table.Td>
                <Table.Td>{yesNo(p.include ?? true)}</Table.Td>
                <Table.Td>{p.order ?? t("client_config.unset")}</Table.Td>
                <Table.Td>{p.relabel ?? t("client_config.unset")}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function Editor({ org, initial }: { org: string; initial: ClientConfig | null }): JSX.Element {
  const [biomarkers, setBiomarkers] = useState<BiomarkerRow[]>(
    () => initial?.biomarkers.map(toBiomarkerRow) ?? [],
  );
  const [panels, setPanels] = useState<PanelRow[]>(() => initial?.panels.map(toPanelRow) ?? []);
  const [newSymbol, setNewSymbol] = useState("");
  const [newPanel, setNewPanel] = useState("");
  const [validated, setValidated] = useState<{
    draft: string;
    result: ClientConfigValidation;
  } | null>(null);
  const [submitted, setSubmitted] = useState<SubmitResult | null>(null);

  const draft = useMemo(
    () => buildClientConfig(org, biomarkers, panels, initial),
    [org, biomarkers, panels, initial],
  );
  const draftKey = JSON.stringify(draft);

  const validate = useMutation({
    mutationFn: () => validateClientConfig(org, draft),
    onSuccess: (result) => setValidated({ draft: draftKey, result }),
  });
  const submit = useMutation({
    mutationFn: () => submitClientConfig(org, draft),
    onSuccess: (result) => setSubmitted(result),
  });

  const current = validated !== null && validated.draft === draftKey ? validated.result : null;
  const stale = validated !== null && validated.draft !== draftKey;
  const canSubmit = current !== null && current.is_valid && !submit.isPending;

  function patchBiomarker(index: number, patch: Partial<BiomarkerRow>): void {
    setBiomarkers((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }
  function patchPanel(index: number, patch: Partial<PanelRow>): void {
    setPanels((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }
  function addBiomarker(): void {
    const symbol = newSymbol.trim();
    if (symbol === "" || biomarkers.some((b) => b.symbol === symbol)) return;
    setBiomarkers((rows) => [
      ...rows,
      { symbol, include: true, drop_if_empty: false, order: null, relabel: "" },
    ]);
    setNewSymbol("");
  }
  function addPanel(): void {
    const name = newPanel.trim();
    if (name === "" || panels.some((p) => p.name === name)) return;
    setPanels((rows) => [...rows, { name, include: true, order: null, relabel: "" }]);
    setNewPanel("");
  }

  const symbolTaken = biomarkers.some((b) => b.symbol === newSymbol.trim());
  const panelTaken = panels.some((p) => p.name === newPanel.trim());

  return (
    <Stack gap="md">
      <Title order={2}>{t("client_config.biomarkers")}</Title>
      {biomarkers.length === 0 ? (
        <Text c="dimmed">{t("client_config.no_biomarker_rules")}</Text>
      ) : (
        <Table withTableBorder aria-label={t("client_config.biomarkers")}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("client_config.col_symbol")}</Table.Th>
              <Table.Th>{t("client_config.col_shown")}</Table.Th>
              <Table.Th>{t("client_config.col_drop_if_empty")}</Table.Th>
              <Table.Th>{t("client_config.col_order")}</Table.Th>
              <Table.Th>{t("client_config.col_relabel")}</Table.Th>
              <Table.Th>{t("client_config.col_remove")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {biomarkers.map((b, i) => (
              <Table.Tr key={b.symbol}>
                <Table.Td ff="monospace">{b.symbol}</Table.Td>
                <Table.Td>
                  <Checkbox
                    aria-label={`${t("client_config.col_shown")} ${b.symbol}`}
                    checked={b.include}
                    onChange={(e) => patchBiomarker(i, { include: e.currentTarget.checked })}
                  />
                </Table.Td>
                <Table.Td>
                  <Checkbox
                    aria-label={`${t("client_config.col_drop_if_empty")} ${b.symbol}`}
                    checked={b.drop_if_empty}
                    onChange={(e) => patchBiomarker(i, { drop_if_empty: e.currentTarget.checked })}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    aria-label={`${t("client_config.col_order")} ${b.symbol}`}
                    value={b.order ?? ""}
                    allowDecimal={false}
                    w={90}
                    onChange={(v) => patchBiomarker(i, { order: orderValue(v) })}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label={`${t("client_config.col_relabel")} ${b.symbol}`}
                    value={b.relabel}
                    onChange={(e) => patchBiomarker(i, { relabel: e.currentTarget.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={`${t("client_config.col_remove")} ${b.symbol}`}
                    onClick={() => setBiomarkers((rows) => rows.filter((_, j) => j !== i))}
                  >
                    x
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <Group align="flex-end" gap="sm">
        <BiomarkerCombobox
          value={newSymbol}
          onChange={setNewSymbol}
          uppercase
          label={t("client_config.add_biomarker")}
          placeholder={t("client_config.add_biomarker_placeholder")}
          aria-label={t("client_config.add_biomarker")}
        />
        <Button
          variant="default"
          onClick={addBiomarker}
          disabled={newSymbol.trim() === "" || symbolTaken}
        >
          {t("client_config.add_biomarker")}
        </Button>
        {symbolTaken && (
          <Text size="sm" c="dimmed">
            {t("client_config.duplicate")}
          </Text>
        )}
      </Group>

      <Title order={2}>{t("client_config.panels")}</Title>
      <Text size="sm" c="dimmed">
        {t("client_config.panel_note")}
      </Text>
      {panels.length === 0 ? (
        <Text c="dimmed">{t("client_config.no_panel_rules")}</Text>
      ) : (
        <Table withTableBorder aria-label={t("client_config.panels")}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("client_config.col_panel")}</Table.Th>
              <Table.Th>{t("client_config.col_shown")}</Table.Th>
              <Table.Th>{t("client_config.col_order")}</Table.Th>
              <Table.Th>{t("client_config.col_relabel")}</Table.Th>
              <Table.Th>{t("client_config.col_remove")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {panels.map((p, i) => (
              <Table.Tr key={p.name}>
                <Table.Td>{p.name}</Table.Td>
                <Table.Td>
                  <Checkbox
                    aria-label={`${t("client_config.col_shown")} ${p.name}`}
                    checked={p.include}
                    onChange={(e) => patchPanel(i, { include: e.currentTarget.checked })}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    aria-label={`${t("client_config.col_order")} ${p.name}`}
                    value={p.order ?? ""}
                    allowDecimal={false}
                    w={90}
                    onChange={(v) => patchPanel(i, { order: orderValue(v) })}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label={`${t("client_config.col_relabel")} ${p.name}`}
                    value={p.relabel}
                    onChange={(e) => patchPanel(i, { relabel: e.currentTarget.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={`${t("client_config.col_remove")} ${p.name}`}
                    onClick={() => setPanels((rows) => rows.filter((_, j) => j !== i))}
                  >
                    x
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <Group align="flex-end" gap="sm">
        <TextInput
          label={t("client_config.add_panel")}
          placeholder={t("client_config.add_panel_placeholder")}
          value={newPanel}
          onChange={(e) => setNewPanel(e.currentTarget.value)}
        />
        <Button
          variant="default"
          onClick={addPanel}
          disabled={newPanel.trim() === "" || panelTaken}
        >
          {t("client_config.add_panel")}
        </Button>
      </Group>

      <Group gap="sm">
        <Button onClick={() => validate.mutate()} loading={validate.isPending}>
          {t("client_config.validate")}
        </Button>
        <Button onClick={() => submit.mutate()} disabled={!canSubmit} loading={submit.isPending}>
          {t("client_config.submit")}
        </Button>
      </Group>

      {validate.isError && (
        <Alert color="red" title={t("client_config.validate_failed")}>
          {validate.error instanceof Error ? validate.error.message : ""}
        </Alert>
      )}
      {stale && <Alert color="yellow">{t("client_config.stale")}</Alert>}
      {current !== null && (
        <Stack gap="sm">
          <Alert
            color={current.is_valid ? "green" : "red"}
            title={current.is_valid ? t("client_config.valid") : t("client_config.invalid")}
          >
            {[...current.errors, ...current.warnings].map((issue) => (
              <Text key={issue.message} size="sm">
                {issue.message}
              </Text>
            ))}
          </Alert>
          {current.diff !== null ? (
            <>
              <Title order={3}>{t("client_config.diff_title")}</Title>
              {current.diff === "" ? (
                <Text c="dimmed">{t("client_config.no_change")}</Text>
              ) : (
                <Code block aria-label={t("client_config.diff_title")}>
                  {current.diff}
                </Code>
              )}
            </>
          ) : (
            <>
              <Title order={3}>{t("client_config.proposed_title")}</Title>
              <Code block aria-label={t("client_config.proposed_title")}>
                {current.proposed_yaml}
              </Code>
            </>
          )}
        </Stack>
      )}
      {submit.isError && (
        <Alert color="red" title={t("client_config.submit_failed")}>
          {submit.error instanceof Error ? submit.error.message : ""}
        </Alert>
      )}
      {submitted !== null && (
        <Alert color="green" title={t("client_config.submitted")}>
          <Anchor href={submitted.mr_url} target="_blank" rel="noreferrer">
            {submitted.mr_url}
          </Anchor>
        </Alert>
      )}
    </Stack>
  );
}

export function ClientConfigPage(): JSX.Element {
  const { org = "" } = useParams<{ org: string }>();
  const permissions = useResolvedPermissions();
  const { data, isPending, isError, error, dataUpdatedAt } = useQuery({
    queryKey: ["client-config", org],
    queryFn: () => getClientConfig(org),
  });

  // A failed permission resolve falls closed to the read-only view.
  const canAuthor =
    permissions.isReady && !permissions.isError && permissions.has("catalog:author");
  const editable = canAuthor && SLUG_RE.test(org);

  return (
    <AppLayout
      breadcrumbs={[
        { label: t("catalogs.title"), to: "/catalogs" },
        { label: org, to: `/catalogs/${encodeURIComponent(org)}` },
        { label: t("client_config.title") },
      ]}
    >
      <Title order={1}>
        {t("client_config.title")}: {org}
      </Title>
      <Text size="sm" c="dimmed">
        {t("client_config.intro")}
      </Text>
      {isPending || !permissions.isReady ? (
        <TableSkeleton label={t("client_config.loading")} />
      ) : isError ? (
        <Alert color="red" title={t("client_config.error_title")}>
          {error instanceof Error ? error.message : ""}
        </Alert>
      ) : editable ? (
        <>
          {data.config === null && <Alert color="blue">{t("client_config.none")}</Alert>}
          {/* Keyed on the load, so a refetch or another org starts a fresh draft. */}
          <Editor key={`${org}:${dataUpdatedAt}`} org={org} initial={data.config} />
        </>
      ) : (
        <>
          {canAuthor ? (
            <Alert color="yellow">{t("client_config.guid_read_only")}</Alert>
          ) : (
            <Text size="sm" c="dimmed">
              {t("client_config.read_only")}
            </Text>
          )}
          {data.config === null ? (
            <Alert color="blue">{t("client_config.none")}</Alert>
          ) : (
            <ReadOnlyView config={data.config} />
          )}
        </>
      )}
    </AppLayout>
  );
}
